import { ClipboardEvent, DragEvent, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { uploadDocumentationAttachment } from '../../services/documentationApi';
import { buildDocsAttachmentSrc } from '../../utils/documentationInlineAttachments';
import { DocumentationMarkdownContent } from './DocumentationMarkdownContent';

/** Limite de imagem embutida no corpo (storage aceita mais; UX mantém teto razoável). */
const DEFAULT_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

type DocumentationMarkdownEditorProps = {
  value: string;
  onChange: (value: string) => void;
  title: string;
  onTitleChange: (title: string) => void;
  pageId: string;
  placeholder?: string;
  maxImageBytes?: number;
};

function isAcceptedImage(file: File): boolean {
  return ACCEPTED_IMAGE_TYPES.has(file.type) || /\.(png|jpe?g|gif|webp)$/i.test(file.name);
}

function extensionForMime(mime: string): string {
  switch (mime) {
    case 'image/jpeg':
      return '.jpg';
    case 'image/gif':
      return '.gif';
    case 'image/webp':
      return '.webp';
    default:
      return '.png';
  }
}

function resolveImageFileName(file: File): string {
  const raw = file.name?.trim();
  if (raw && raw !== 'image.png' && /\.[^.]+$/.test(raw)) {
    return raw;
  }
  return `imagem${extensionForMime(file.type || 'image/png')}`;
}

function insertAtCursor(value: string, cursor: number, snippet: string): { next: string; caret: number } {
  const before = value.slice(0, cursor);
  const after = value.slice(cursor);
  const needsLeadingNewline = before.length > 0 && !before.endsWith('\n');
  const needsTrailingNewline = after.length > 0 && !after.startsWith('\n');
  const block = `${needsLeadingNewline ? '\n' : ''}${snippet}${needsTrailingNewline ? '\n' : ''}`;
  return {
    next: `${before}${block}${after}`,
    caret: before.length + block.length,
  };
}

/**
 * Editor Markdown para páginas de documentação.
 * Colar/arrastar/inserir imagem envia o arquivo ao storage e grava docs-attachment:{id}.
 */
export function DocumentationMarkdownEditor({
  value,
  onChange,
  title,
  onTitleChange,
  pageId,
  placeholder = 'Escreva a documentação em Markdown…\n\nDica: cole ou arraste uma imagem aqui.',
  maxImageBytes = DEFAULT_MAX_IMAGE_BYTES,
}: DocumentationMarkdownEditorProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function insertImageFiles(files: FileList | File[]): Promise<void> {
    const images = Array.from(files).filter(isAcceptedImage);
    if (images.length === 0) {
      toast.error('Cole uma imagem PNG, JPEG, GIF ou WebP.');
      return;
    }

    const oversized = images.find((file) => file.size > maxImageBytes);
    if (oversized) {
      toast.error(`A imagem deve ter no máximo ${Math.round(maxImageBytes / (1024 * 1024))} MB.`);
      return;
    }

    if (!pageId) {
      toast.error('Salve a página antes de inserir imagens.');
      return;
    }

    setUploading(true);
    try {
      const textarea = textareaRef.current;
      let cursor = textarea?.selectionStart ?? value.length;
      let nextValue = value;

      for (const file of images) {
        const uploaded = await uploadDocumentationAttachment(pageId, file);
        const alt = resolveImageFileName(file).replace(/\.[^.]+$/, '') || 'imagem';
        const snippet = `![${alt}](${buildDocsAttachmentSrc(uploaded.id)})`;
        const inserted = insertAtCursor(nextValue, cursor, snippet);
        nextValue = inserted.next;
        cursor = inserted.caret;
      }

      onChange(nextValue);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(cursor, cursor);
      });
    } catch {
      toast.error('Não foi possível enviar a imagem ao storage.');
    } finally {
      setUploading(false);
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLTextAreaElement>): void {
    const clipboard = event.clipboardData;
    if (!clipboard) return;

    const imageFiles: File[] = [];
    for (const item of Array.from(clipboard.items)) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length === 0 && clipboard.files?.length) {
      imageFiles.push(...Array.from(clipboard.files).filter(isAcceptedImage));
    }

    if (imageFiles.length === 0) return;

    event.preventDefault();
    void insertImageFiles(imageFiles);
  }

  function handleDrop(event: DragEvent<HTMLTextAreaElement>): void {
    const files = event.dataTransfer?.files;
    if (!files?.length) return;
    const images = Array.from(files).filter(isAcceptedImage);
    if (images.length === 0) return;
    event.preventDefault();
    void insertImageFiles(images);
  }

  return (
    <div className="docs-md-editor">
      <input
        type="text"
        className="docs-md-editor__title"
        value={title}
        onChange={(event) => onTitleChange(event.target.value)}
        placeholder="Título da página"
        aria-label="Título da página"
      />
      <div className="docs-md-editor__toolbar">
        <span className="docs-md-editor__format-badge">Markdown</span>
        <div className="docs-md-editor__toolbar-actions">
          <button
            type="button"
            className="erp-btn-secondary text-xs"
            disabled={uploading || showPreview}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? 'Enviando…' : 'Inserir imagem'}
          </button>
          <button
            type="button"
            className="erp-btn-secondary text-xs"
            onClick={() => setShowPreview((current) => !current)}
          >
            {showPreview ? 'Editar' : 'Pré-visualizar'}
          </button>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        multiple
        onChange={(event) => {
          const files = event.target.files;
          if (files?.length) void insertImageFiles(files);
          event.target.value = '';
        }}
      />
      {showPreview ? (
        <div className="docs-wiki-prose docs-md-editor__preview">
          <DocumentationMarkdownContent
            value={value}
            pageId={pageId}
            emptyLabel="Nada para pré-visualizar."
          />
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          className="docs-md-editor__textarea"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={(event) => {
            if (event.dataTransfer?.types?.includes('Files')) {
              event.preventDefault();
            }
          }}
          placeholder={placeholder}
          spellCheck={false}
        />
      )}
    </div>
  );
}
